import { useHydrateFileRecordEntity } from "@/api/fileRecords";
import { useFlowMessages } from "@/api/messages";
import { PaneBackButton } from "@/components/PaneBackButton";
import { usePaneRouterContextSafe } from "@/components/PaneRouter";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ROUTES } from "@/constants/routes";
import { useOnScreenEditorsContext } from "@/context/OnScreenEditorsContext";
import { useAppNavigate, usePaneParams } from "@/hooks/pane-routing";
import {
  CANVAS_DETAIL_PANE_HEADER_ACTIONS_CLASS,
  CANVAS_DETAIL_PANE_HEADER_CLASS,
} from "@/panes/canvasDetailPaneHeaderClasses";
import {
  useOpenFileRecord,
  useOpenLegacyEntityAsFileRecord,
} from "@/panes/file-record-detail";
import analytics from "@/utils/analytics";
import { useGlobalEntityStore } from "@/utils/globalEntityStore";
import { route } from "@/utils/route";
import {
  ConnectionPayload,
  Entity,
  EntityCard,
  getEntityData,
  getEntityName,
  getEntityType,
  INSERT_CONNECTION_COMMAND,
  legacyFileEntityDataToFileRecord,
  PaneLayout,
  PaneLayoutContent,
  PaneLayoutHeader,
  PaneLayoutHeaderActions,
} from "@cohesive/ui-components";
import { Database, X } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";

/** Hydrates a wire file_record entity and renders it as a compact card. */
function SourceFileRecordCard({
  entity,
  flowSessionId,
}: {
  entity: Entity;
  flowSessionId: string;
}) {
  const openFileRecord = useOpenFileRecord();
  const { entity: hydrated, isLoading } = useHydrateFileRecordEntity(
    "base" in entity && entity.base === "file_record"
      ? (entity as { base: "file_record"; data: unknown; platform: string })
      : null,
    { class: "flow-session", flow_session_id: flowSessionId },
  );

  if (isLoading) return <Skeleton className="h-[60px] w-full rounded-xl" />;
  if (!hydrated) return null;

  const id = String((hydrated as any).data?.id ?? "");
  return (
    <EntityCard
      entity={hydrated as Entity}
      variant="compact"
      onClick={() => openFileRecord(id, flowSessionId)}
    />
  );
}

export function EntitiesPane() {
  const appNavigate = useAppNavigate();
  const paneContext = usePaneRouterContextSafe();
  const openFileRecord = useOpenFileRecord();

  const { onScreenEditors } = useOnScreenEditorsContext();

  const { flowSessionId, flowMessageId } = usePaneParams(ROUTES.ENTITIES.LIST);
  const openLegacy = useOpenLegacyEntityAsFileRecord();

  const { data: messages } = useFlowMessages(flowSessionId);
  const globalEntityStore = useGlobalEntityStore(messages ?? []);

  // Find the specific message and get its entity IDs
  const message = messages?.find((m) => m.id === flowMessageId);

  // Collect entity IDs from inline_entity_refs or result.entities
  const entityIds = new Set<string>();

  // Handle inline_entity_refs (for later messages with entity references)
  const messageResult = message?.result;
  if (
    messageResult?.inline_entity_refs &&
    Array.isArray(messageResult.inline_entity_refs)
  ) {
    messageResult.inline_entity_refs.forEach((id: string) => entityIds.add(id));
  }

  // Handle full entity objects (for first messages)
  if (message?.result?.entities) {
    message.result.entities.forEach((entity) => {
      const entityData = getEntityData(entity);
      if (entityData?.id) {
        entityIds.add(entityData.id.toString());
      }
    });
  }

  // Build entities map by looking up IDs in the global store
  const entities = new Map<string, Entity>();
  entityIds.forEach((id) => {
    const entity = globalEntityStore.get(id);
    if (entity) {
      entities.set(id, entity);
    }
  });

  if (!entities?.size) {
    return null;
  }

  const addMention = (entity: Entity) => {
    const sideEditor = onScreenEditors.find(
      (editor) => editor.location === "sidebar",
    );

    const mainEditor = onScreenEditors.find(
      (editor) => editor.location === "main",
    );

    const editorToUse = sideEditor || mainEditor;

    const entityData = getEntityData(entity);

    if (!entityData) {
      return;
    }

    const e = {
      // Generic entities fix `platform` to "generic" and carry the real
      // source platform in `data.platform_name` — prefer that so the mention
      // still identifies e.g. Zoom/Gmail rather than just "generic".
      platform: entityData?.platform_name || entity.platform,
      type: getEntityType(entity),
      identifier: entityData.id?.toString().replaceAll(":", "%3A"),
      title: getEntityName(entity),
    } as ConnectionPayload;

    editorToUse?.editor.dispatchCommand(INSERT_CONNECTION_COMMAND, e);

    analytics.trackConnectorEntitySelected({
      platform: e.platform,
      entityType: e.type,
      surface: "entities_pane",
    });
  };

  return (
    <PaneLayout>
      <PaneLayoutHeader className={CANVAS_DETAIL_PANE_HEADER_CLASS}>
        <PaneLayoutHeaderActions
          className={CANVAS_DETAIL_PANE_HEADER_ACTIONS_CLASS}
        >
          <PaneBackButton />
          <div className="flex min-w-0 flex-1 items-center gap-1.5 pl-1">
            <Database className="h-4 w-4 shrink-0 text-blue-500" />
            <span className="text-sm font-medium truncate">Sources</span>
          </div>
          <TooltipProvider>
            {paneContext && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="toolbarGhost"
                    size="icon-sm"
                    className="shrink-0"
                    onClick={() => appNavigate({ pane: "rightPane", to: null })}
                    aria-label="Close"
                  >
                    <X />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Close</TooltipContent>
              </Tooltip>
            )}
          </TooltipProvider>
        </PaneLayoutHeaderActions>
      </PaneLayoutHeader>
      <PaneLayoutContent className="!px-0 !pt-0">
        <AnimatePresence>
          {Array.from(entities).map(([entityId, entity], i) => {
            // File-record-backed entities (googleai_image/video, uploaded
            // attachments) don't have a `base` branch in the shared
            // `EntityCard` switch — their payload is just `{id, title}` per
            // the unified `base-file-record` schema. Fetch the full record
            // and render the standard `FileRecordCard` so Sources matches
            // the inline chat rendering and the workspace list.
            const baseValue = (entity as unknown as { base?: string }).base;
            if (baseValue === "file_record") {
              return (
                <motion.div
                  key={entityId}
                  initial={{ opacity: 0, y: 0 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{
                    opacity: 0,
                    y: 0,
                    transition: { duration: 0, delay: 0 },
                  }}
                  transition={{ delay: i * 0.025 }}
                  className="border-b last:border-b-0 p-3"
                >
                  <SourceFileRecordCard
                    entity={entity}
                    flowSessionId={flowSessionId}
                  />
                </motion.div>
              );
            }

            return (
              <motion.div
                key={entityId}
                initial={{ opacity: 0, y: 0 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{
                  opacity: 0,
                  y: 0,
                  transition: { duration: 0, delay: 0 },
                }}
                transition={{ delay: i * 0.025 }}
                className="border-b last:border-b-0"
              >
                <EntityCard
                  key={entityId}
                  entity={entity}
                  onClick={() => {
                    if ("type" in entity && (entity as any).type === "skill") {
                      appNavigate({
                        pane: "rightPane",
                        to: route(ROUTES.SKILLS.SHOW, {
                          id: (entity.data as any).id.toString(),
                        }),
                      });
                      return;
                    }

                    // File-record-backed entities (googleai image/video,
                    // uploaded attachments) route to the unified
                    // `FileRecordDetailPane`. `entity.data.id` is the
                    // file_record UUID per `base-file-record` schema.
                    const baseValue = (entity as unknown as { base?: string })
                      .base;
                    if (baseValue === "file_record") {
                      const dataId = (
                        entity as unknown as { data?: { id?: unknown } }
                      ).data?.id;
                      if (dataId != null) {
                        openFileRecord(String(dataId), flowSessionId);
                      }
                      return;
                    }

                    // Legacy image/video entities — adapt and open via unified pane.
                    if (
                      "base" in entity &&
                      (entity.base === "image" || entity.base === "video")
                    ) {
                      const record = legacyFileEntityDataToFileRecord(
                        entity.base as "image" | "video",
                        entity.data as unknown as Record<string, unknown>,
                      );
                      if (record) openLegacy(record);
                      return;
                    }
                  }}
                  onMentionClick={() => {
                    // Always allow mention functionality via the mention button
                    addMention(entity);
                  }}
                  className="rounded-none shadow-none border-none"
                />
              </motion.div>
            );
          })}
        </AnimatePresence>
      </PaneLayoutContent>
    </PaneLayout>
  );
}
